import { minuteMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import {
    getGetDeviceQueryOptions as deviceDetailQuery,
    getListDevicesQueryOptions as deviceListQuery,
} from "@/api/orval/generated/device";
import type { Device, PollForUpdates200, RecentDataItem } from "@/api/orval/generated/schemas";
import { getGetRecentSessionDataQueryOptions, pollForUpdates } from "@/api/orval/generated/session";
import {
    getGetThirdPartyDeviceQueryOptions as thirdPartyDeviceDetailQuery,
    getListThirdPartyDevicesQueryOptions as thirdPartyDeviceListQuery,
} from "@/api/orval/generated/third-party-device";
import { patchCollection } from "@/api/query-cache/collection-cache-writers";
import { queryClient } from "@/api/queryClient";

/**
 * Variable to store the most recent poll time
 */
let mostRecentPollTime = new Date();
SessionHandler.onSessionLoad.addListener(() => {
    mostRecentPollTime = new Date();
});

interface RefreshChangedDevicesParams {
    includeRecentData?: boolean;
    includeUpdatedDevices?: boolean;
    includeSpecificDeviceIDs?: string[];
}

/**
 * Data is timestamped at the time of reading, not when data is updated in the table.
 * This means that data that takes longer than a polling interval to go from measurement
 * to being updated in the table will be skipped as it will be older than the last poll time.
 *
 * We add a buffer of 3 uplink cycles (2 minute cycle on gen3 and later devices) to ensure no data is missed.
 */
const LAST_POLL_TIME_BUFFER_MS = 6 * minuteMs;

/**
 * Refresh all the devices that have changed since the last poll
 *
 * The poll narrows which devices come back, not which fields: each one arrives as a whole document,
 * including schema defaults. That is what lets the cache writers below treat an update as the entity
 * itself rather than a patch on top of one.
 *
 * @param includeRecentData - Include most recent data points
 * @param includeUpdatedDevices - Include devices that have been updated since the last poll
 * @param includeSpecificDeviceIDs - node_ids to force to be included
 * @return {Promise<void>}
 */
export const refreshChangedDevices = async ({
    includeRecentData = true,
    includeUpdatedDevices = true,
    includeSpecificDeviceIDs = undefined,
}: RefreshChangedDevicesParams) => {
    // The cache is scoped by curUser at write time, so pin the org we asked for
    const requestedOrg = SessionHandler.curUser;
    if (!requestedOrg) return;

    try {
        const newData = await pollForUpdates({
            includeRecentData,
            includeUpdatedDevices,
            lastPollTime: new Date(mostRecentPollTime.getTime() - LAST_POLL_TIME_BUFFER_MS),
            includeSpecificDevices: includeSpecificDeviceIDs ?? [],
        });

        // An org switch mid-flight would land this payload under the new org's scope, in both caches.
        if (SessionHandler.curUser !== requestedOrg) return;

        // Advanced before the writes below so a throw in either cannot re-poll the same window.
        mostRecentPollTime = new Date(newData.timeOfPoll ?? new Date());

        // Written to both caches: legacy screens read MasterIndex, generated query hooks read the
        // TanStack cache. The MasterIndex half goes away as those screens migrate.
        updateMasterIndexFromJson(newData);
        updateQueryCacheFromPoll(newData);
    } catch (error) {
        console.error("Error polling devices", error);
    }
};

/**
 * Writes one device document into the MasterIndex model the legacy screens read from. Exported for
 * writes that come back with fresh device state of their own rather than through the poll.
 */
export const syncDeviceToMasterIndex = (deviceUpdate: Device) => {
    const deviceObject =
        MasterIndex.irrigationDevice.byID[deviceUpdate.node_id] ?? MasterIndex.blockValve.byID[deviceUpdate.node_id];
    if (!deviceObject) {
        return;
    }
    deviceObject.assignFromJSON(deviceUpdate);
    deviceObject.lastRefreshed = new Date();
    deviceObject.onChange.trigger({});
};

/**
 * Update the master index data structure from the raw json data
 */
const updateMasterIndexFromJson = (json: PollForUpdates200) => {
    json.deviceUpdates?.forEach(syncDeviceToMasterIndex);

    // The server returns one recent-data document per owner, so there is at most one — but an org with no
    // readings yet has none.
    const [recentData] = json.recentData ?? [];
    if (recentData) {
        // Trigger the legacy event listener
        SessionHandler.onRecentDataUpdated.trigger({
            affectedDevices: updateRecentData(recentData),
        });
    }

    // Untyped where deviceUpdates above is typed: the generated ThirdPartyDevice DTO leaves schemaKey
    // optional, while the web-client model it feeds requires it.
    json.thirdPartyDeviceUpdates?.forEach((deviceUpdate: any) => {
        const deviceObject = MasterIndex.thirdPartyDevice.byID[deviceUpdate.node_id];
        if (!deviceObject) {
            return;
        }
        deviceObject?.assignFromJSON(deviceUpdate);
        deviceObject.lastRefreshed = new Date();
        deviceObject.onChange.trigger({});
    });
};

/**
 * Iterates over the recent data and updates all recentData items that have newer data
 */
const updateRecentData = (recentData: RecentDataItem) => {
    const idsUpdated = new Set<string>();

    Object.entries(recentData.data).forEach(([recentDataKey, dataPoint]) => {
        const [dataKey, deviceId] = recentDataKey.split("__");
        const deviceObject =
            MasterIndex.blockValve.byID[deviceId] ??
            MasterIndex.irrigationDevice.byID[deviceId] ??
            MasterIndex.thirdPartyDevice.byID[deviceId] ??
            MasterIndex.SensoterraMoisture.byID[deviceId];

        // An undated reading cannot be ordered against what is already cached, so there is nothing to apply.
        if (!deviceObject || !dataPoint.date) {
            return;
        }
        // @ts-ignore recentData[dataKey] does exist on the device object but isnt typed properly
        const existingDataPoint = deviceObject.recentData[dataKey];

        if (!existingDataPoint || new Date(dataPoint.date) > new Date(existingDataPoint.date)) {
            // @ts-ignore recentData[dataKey] does exist on the device object but isnt typed properly
            deviceObject.recentData[dataKey] = {
                value: dataPoint.value,
                date: new Date(dataPoint.date),
            };
            idsUpdated.add(deviceId);
        }
    });
    return idsUpdated;
};

/**
 * Fans one poll payload across the device caches, so screens reading through a generated query hook see
 * what polling refreshed.
 *
 * The owner's whole recent-data collection rather than a delta, so it replaces outright. Sensoterra
 * readings ride inside it, keyed by device, which is why no Sensoterra collection is fanned out here.
 */
function updateQueryCacheFromPoll(pollResponse: PollForUpdates200): void {
    patchCollection(deviceListQuery, deviceDetailQuery, pollResponse.deviceUpdates);
    patchCollection(thirdPartyDeviceListQuery, thirdPartyDeviceDetailQuery, pollResponse.thirdPartyDeviceUpdates);

    // An empty array means either "no readings" or "the poll did not ask for them", so it is skipped (instead of written as a wipe)
    if (pollResponse.recentData?.length) {
        // Written directly rather than through patchCollection: being the whole collection, this is as
        // good as a fetch, so it may settle the query where a partial entity update may not.
        queryClient.setQueryData(getGetRecentSessionDataQueryOptions().queryKey, pollResponse.recentData);
    }
}
